import { isVerifiedProvider } from './verifiedProvider';

/**
 * Result tiles in three sizes — pure functions, no React, deterministic (SSR = first client render).
 *
 * Two references, one rule each:
 *
 * - Airbnb keeps its result grid uniform and marks quality with a label on the photo ("Gäste-
 *   Favorit") and with its ranking. We take the card anatomy (photo, text below, no frame), the
 *   label and the ranking.
 * - iOS widgets come in a size family that snaps to one grid: a medium widget is two small ones
 *   plus the gap, at the same height; a large one is two by two. And a larger widget shows more,
 *   not the same content stretched (Apple HIG, Widgets: "avoid expanding a smaller widget's
 *   content to simply fill a larger area"). We take the size family: a small tile shows one photo,
 *   a medium tile two, a large tile three — each a different view of the space.
 *
 * Who gets the larger tile: the spaces a visitor can most safely book. A verified provider can
 * take payment at once (`isVerifiedProvider`); a space that was booked through cowork24 before has
 * proven it (`bookedCountOf`). Both → large, one of them → medium, neither → small.
 *
 * The size never changes the order — ranking decides the order, the tile only how much room a
 * listing gets in it.
 */

export const TILE_SMALL = 'small';
export const TILE_MEDIUM = 'medium';
export const TILE_LARGE = 'large';

/** Photos each size shows: one more per step (see above). */
export const TILE_PHOTO_COUNT = { [TILE_SMALL]: 1, [TILE_MEDIUM]: 2, [TILE_LARGE]: 3 };

/**
 * Small tiles between two larger ones. Without a budget the size would say nothing: 91 % of the
 * Zurich meeting rooms come from verified providers (2026-09-27), so every card would be large.
 * Even on purpose — next to the map the list has two columns, and an even count of small tiles
 * lets every larger tile start its own row there instead of leaving a hole behind it.
 */
export const TILE_MIN_GAP = 6;

/**
 * How often a space was booked through cowork24. Written into the listing's public metadata by
 * the daily job (scripts/sync-listing-booking-stats.js) — only the Integration API can write
 * metadata, so a provider cannot raise it. Owner tests and internal accounts are not counted.
 *
 * @param {Object} listing denormalised listing entity
 * @returns {number} 0 when unknown
 */
export const bookedCountOf = listing => {
  const count = listing?.attributes?.metadata?.bookedCount;
  return Number.isInteger(count) && count > 0 ? count : 0;
};

/**
 * @param {Object} listing denormalised listing entity (with `author` for the verified badge)
 * @returns {0|1|2} one point for a verified provider, one for a booking that happened
 */
export const prominenceOf = listing =>
  (isVerifiedProvider(listing?.author) ? 1 : 0) + (bookedCountOf(listing) > 0 ? 1 : 0);

const sizeForProminence = prominence =>
  prominence >= 2 ? TILE_LARGE : prominence === 1 ? TILE_MEDIUM : TILE_SMALL;

/**
 * Most prominent first, the given order inside each level (a stable partition). For lists whose
 * order carries no relevance of its own — /s without a chosen sort or keywords returns the newest
 * listings first. A sort the visitor chose is never overridden; call this only without one.
 *
 * @param {Array} listings
 * @returns {Array} new array
 */
export const rankByProminence = listings =>
  (listings || [])
    .map((listing, index) => ({ listing, index, prominence: prominenceOf(listing) }))
    .sort((a, b) => b.prominence - a.prominence || a.index - b.index)
    .map(({ listing }) => listing);

/**
 * The size of every tile, in list order. A listing gets the size it earns only once at least
 * `minGap` small tiles (an even number of them) stand between it and the previous larger tile;
 * otherwise it stays small, keeping its place. Prefix-stable: revealing more cards never changes
 * the tiles already shown.
 *
 * @param {Array} listings in display order
 * @param {{ minGap?: number }} [options]
 * @returns {Array<'small'|'medium'|'large'>}
 */
export const planTileSizes = (listings, { minGap = TILE_MIN_GAP } = {}) => {
  let seenLarger = false;
  // Small tiles since the start of the list or since the last larger tile.
  let smalls = 0;
  return (listings || []).map(listing => {
    const earned = sizeForProminence(prominenceOf(listing));
    // Before the first larger tile only the row parity counts: it may be the very first card.
    const fits = smalls % 2 === 0 && (!seenLarger || smalls >= minGap);
    if (earned !== TILE_SMALL && fits) {
      seenLarger = true;
      smalls = 0;
      return earned;
    }
    smalls += 1;
    return TILE_SMALL;
  });
};
